'use client';

import { x } from '@xstyled/styled-components';

import houseArt from '../assets/house-cross-section.svg';
import {
  ApplianceId,
  batteryCapacityEu,
  GameState,
  hourOfDay,
  isDarkOut,
  ScenarioId,
  ShopItemId,
  solarGenEuPerHour,
  weatherSolarFactor,
} from '../engine';

type Props = { state: GameState };

// The dollhouse is artwork — its palette is intentionally self-contained, like an image.
// Blueprint paper tones come from the Borealis navy ramp; the glow accent is brand amber.
const INK = '#272525';
const INK_INVERTED = '#dde5f7';
const GLOW = '#fec351';
const PAPER = {
  dark: '#0d1429',
  day: {
    [ScenarioId.DEEP_FREEZE]: '#edf2fb',
    [ScenarioId.LOVELY_SPRING_DAY]: '#dde5f7',
    [ScenarioId.SUMMERTIME_STORMS]: '#d7e1f4',
  },
  dusk: '#efe3d3',
  night: '#162548',
  stormy: '#b7c4e4',
};

// Scene window into the house art's own coordinate space (viewBox 0 0 4241.4 2393.3).
const VIEW = { h: 1450, w: 2790, x: 555, y: 375 };

type Phase = 'dawn' | 'day' | 'night';

const dayPhase = (gameMinutes: number): Phase => {
  const hod = hourOfDay(gameMinutes);
  if (hod >= 20 || hod < 6) return 'night';
  if (hod < 8 || hod >= 18) return 'dawn';
  return 'day';
};

const isStormy = (state: GameState): boolean =>
  state.prep.scenarioId === ScenarioId.SUMMERTIME_STORMS &&
  dayPhase(state.gameMinutes) === 'day' &&
  weatherSolarFactor(state) < 0.5;

const paperColor = (state: GameState): string => {
  const phase = dayPhase(state.gameMinutes);
  if (phase === 'night') return state.dark ? PAPER.dark : PAPER.night;
  if (phase === 'dawn') return PAPER.dusk;
  return isStormy(state) ? PAPER.stormy : PAPER.day[state.prep.scenarioId];
};

// Lit-room pools of light, in art coordinates (the cross-section has no windows,
// so powered rooms glow instead — same affordance as the v1 window glow).
const ROOM_GLOWS = [
  { h: 230, w: 770, x: 1675, y: 910 }, // attic bedroom
  { h: 332, w: 764, x: 1660, y: 1151 }, // living + dining
  { h: 332, w: 450, x: 2430, y: 1151 }, // kitchen
  { h: 290, w: 1218, x: 1660, y: 1497 }, // basement
  { h: 310, w: 316, x: 1266, y: 1436 }, // garage-side room
];

// Where each running appliance lights up on the cross-section.
const APPLIANCE_SPOTS: Record<ApplianceId, { x: number; y: number }> = {
  [ApplianceId.DISHWASHER]: { x: 2640, y: 1450 },
  [ApplianceId.EV_CHARGER]: { x: 920, y: 1465 },
  [ApplianceId.FRIDGE]: { x: 2492, y: 1440 },
  [ApplianceId.HVAC]: { x: 3180, y: 1515 },
  [ApplianceId.LAUNDRY]: { x: 1830, y: 1685 },
  [ApplianceId.LIGHTS]: { x: 2200, y: 1265 },
  [ApplianceId.OVEN]: { x: 2790, y: 1450 },
  [ApplianceId.TV_CONSOLE]: { x: 2310, y: 1680 },
  [ApplianceId.WATER_HEATER]: { x: 2762, y: 1700 },
  [ApplianceId.WIFI]: { x: 1700, y: 1320 },
};

// Roof-mounted solar strips matching the art's original panel placement (20.2° pitch).
const SOLAR_STRIPS = [
  { x: 2502, y: 802 },
  { x: 2733, y: 887 },
  { x: 2338, y: 871 },
  { x: 2569, y: 956 },
];

const RAIN_DROPS = Array.from({ length: 16 }, (_, i) => ({
  x: VIEW.x + 130 + i * 160,
  y: VIEW.y + 60 + (i % 4) * 70,
}));
const SNOW_FLAKES = Array.from({ length: 14 }, (_, i) => ({
  x: VIEW.x + 180 + i * 180,
  y: VIEW.y + 50 + (i % 5) * 60,
}));

const Sun = ({ gameMinutes }: { gameMinutes: number }) => {
  const hod = Math.min(Math.max(hourOfDay(gameMinutes), 7), 19);
  const t = (hod - 7) / 12;
  const cx = VIEW.x + 260 + t * (VIEW.w - 520);
  const cy = VIEW.y + 250 - Math.sin(t * Math.PI) * 160;
  return (
    <g stroke={INK} strokeWidth="6">
      <circle cx={cx} cy={cy} fill={GLOW} fillOpacity="0.9" r="42" />
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i * Math.PI) / 4;
        return (
          <line
            key={a}
            x1={cx + Math.cos(a) * 58}
            x2={cx + Math.cos(a) * 78}
            y1={cy + Math.sin(a) * 58}
            y2={cy + Math.sin(a) * 78}
          />
        );
      })}
    </g>
  );
};

const Moon = () => (
  <g stroke={INK_INVERTED} strokeWidth="6">
    <circle cx={VIEW.x + VIEW.w - 320} cy={VIEW.y + 160} fill={INK_INVERTED} fillOpacity="0.25" r="36" />
    <circle cx={VIEW.x + VIEW.w - 332} cy={VIEW.y + 150} fill="none" r="9" strokeWidth="4" />
    <circle cx={VIEW.x + VIEW.w - 306} cy={VIEW.y + 174} fill="none" r="5" strokeWidth="4" />
  </g>
);

const StormClouds = ({ ink }: { ink: string }) => (
  <g fill="none" stroke={ink} strokeLinecap="round" strokeWidth="7">
    <path d="M900 560a70 70 0 0 1 6-140 95 95 0 0 1 185-12 68 68 0 0 1 12 152Z" />
    <path d="M2620 520a60 60 0 0 1 5-120 82 82 0 0 1 160-10 58 58 0 0 1 10 130Z" />
  </g>
);

export const Dollhouse = ({ state }: Props) => {
  const darkOut = isDarkOut(state.gameMinutes);
  const night = dayPhase(state.gameMinutes) === 'night';
  const powered = !state.dark;
  const capacity = batteryCapacityEu(state.prep);
  const batteryFrac = capacity > 0 ? state.batteryEu / capacity : 0;
  const largeSolar = Boolean(state.prep.purchases[ShopItemId.SOLAR_LARGE]);
  const hasSolar = largeSolar || Boolean(state.prep.purchases[ShopItemId.SOLAR_SMALL]);
  const solarGen = solarGenEuPerHour(state);
  const scenario = state.prep.scenarioId;
  const ink = night ? INK_INVERTED : INK;
  const strips = largeSolar ? SOLAR_STRIPS : SOLAR_STRIPS.slice(0, 2);
  const glowOpacity = darkOut ? 0.5 : 0.16;
  const gaugeH = Math.max(6, 86 * batteryFrac);

  return (
    <x.div borderRadius={12} overflow="hidden" w="100%">
      <svg
        aria-label="Your house"
        role="img"
        viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter height="180%" id="bb-soft" width="180%" x="-40%" y="-40%">
            <feGaussianBlur stdDeviation="34" />
          </filter>
          <pattern height="84" id="bb-grid" patternUnits="userSpaceOnUse" width="84">
            <path d="M84 0H0v84" fill="none" stroke={ink} strokeOpacity="0.07" strokeWidth="2" />
          </pattern>
        </defs>

        {/* blueprint paper + drafting grid */}
        <rect fill={paperColor(state)} height={VIEW.h} width={VIEW.w} x={VIEW.x} y={VIEW.y} />
        <rect fill="url(#bb-grid)" height={VIEW.h} width={VIEW.w} x={VIEW.x} y={VIEW.y} />

        {!darkOut && <Sun gameMinutes={state.gameMinutes} />}
        {night && <Moon />}
        {isStormy(state) && <StormClouds ink={ink} />}

        {isStormy(state) && (
          <g stroke={ink} strokeLinecap="round" strokeOpacity="0.7" strokeWidth="5">
            {RAIN_DROPS.map(({ x: rx, y: ry }) => (
              <line key={`${rx}-${ry}`} x1={rx} x2={rx - 16} y1={ry} y2={ry + 52} />
            ))}
          </g>
        )}
        {scenario === ScenarioId.DEEP_FREEZE && (
          <g fill={night ? INK_INVERTED : '#ffffff'} stroke={ink} strokeOpacity="0.45" strokeWidth="2">
            {SNOW_FLAKES.map(({ x: sx, y: sy }) => (
              <circle cx={sx} cy={sy} key={`${sx}-${sy}`} r="8" />
            ))}
          </g>
        )}

        {/* lit rooms glow while the house has power */}
        {powered && (
          <g fill={GLOW} filter="url(#bb-soft)" opacity={glowOpacity}>
            {ROOM_GLOWS.map(({ h, w, x: gx, y: gy }) => (
              <rect height={h} key={`${gx}-${gy}`} rx={40} width={w} x={gx} y={gy} />
            ))}
          </g>
        )}

        {/* the brand whole-home cross-section, inverted to white-line blueprint at night */}
        <x.image filter={night ? 'invert(0.88)' : 'none'} height="2393.3" href={houseArt.src} width="4241.4" />

        {/* roof solar, present only when purchased */}
        {hasSolar && (
          <g>
            {strips.map(({ x: sx, y: sy }) => (
              <rect
                fill={ink}
                height="14"
                key={`${sx}-${sy}`}
                transform={`rotate(20.2 ${sx} ${sy})`}
                width="235"
                x={sx}
                y={sy}
              />
            ))}
            {solarGen > 0.2 &&
              strips.map(({ x: sx, y: sy }) => (
                <line
                  key={`glint-${sx}-${sy}`}
                  stroke={GLOW}
                  strokeLinecap="round"
                  strokeWidth="6"
                  transform={`rotate(20.2 ${sx} ${sy})`}
                  x1={sx + 18}
                  x2={sx + 217}
                  y1={sy - 8}
                  y2={sy - 8}
                />
              ))}
          </g>
        )}

        {/* wall battery with charge gauge, present only when purchased */}
        {capacity > 0 && (
          <g>
            <rect
              fill={night ? PAPER.night : '#ffffff'}
              height="101"
              rx="8"
              stroke={ink}
              strokeWidth="5"
              width="50"
              x="3105"
              y="1206"
            />
            <rect
              fill={batteryFrac > 0.3 ? '#7ddb8a' : '#e76f51'}
              height={gaugeH}
              rx="4"
              width="34"
              x="3113"
              y={1299 - gaugeH}
            />
            <path d="M3133 1238l-10 22h12l-10 22" fill="none" stroke={ink} strokeLinejoin="round" strokeWidth="4" />
            <line stroke={ink} strokeWidth="5" x1="3130" x2="3130" y1="1307" y2="1345" />
          </g>
        )}

        {/* running appliances light up where they live in the house */}
        {powered &&
          state.activeLoads.map(({ applianceId }) => {
            const { x: ax, y: ay } = APPLIANCE_SPOTS[applianceId];
            return (
              <g key={applianceId}>
                <circle cx={ax} cy={ay} fill={GLOW} filter="url(#bb-soft)" opacity="0.55" r="80" />
                <circle cx={ax} cy={ay} fill={GLOW} opacity="0.9" r="16" stroke={ink} strokeWidth="4" />
              </g>
            );
          })}

        {/* a lone candle when the battery dies */}
        {!powered && (
          <g>
            <circle cx="2050" cy="1430" fill={GLOW} filter="url(#bb-soft)" opacity="0.5" r="90" />
            <rect fill={ink} height="40" width="14" x="2043" y="1440" />
            <ellipse cx="2050" cy="1424" fill={GLOW} rx="9" ry="15" />
          </g>
        )}
      </svg>
    </x.div>
  );
};
